import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search,
  X,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Filter,
  Bookmark,
  PackageSearch,
} from 'lucide-react';
import { Dropdown } from '@/components/common/Dropdown';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { useCart } from '@/lib/cart-context';
import { useClientMode } from '@/lib/client-mode';
import { foldSearch } from '@/lib/search';
import { ProductCard } from '@/components/products/ProductCard';
import { ProductDetailModal } from '@/components/products/ProductDetailModal';
import { RequestProductDialog } from '@/components/products/RequestProductDialog';
import { CourseOnboardingBanner } from '@/components/formation/CourseOnboardingBanner';
import { Pagination } from '@/components/common/Pagination';
import {
  useProArticles,
  useProArticlePricing,
  useProPublishedCategories,
  useProFavorites,
} from '@/hooks/use-pro-articles';
import type { ProArticle, ProArticlePricing } from '@erp/domain/pro';

type Sort = 'popular' | 'price_asc' | 'price_desc' | 'name';

const FILTERS_STORAGE_KEY = 'pro_home_filters_v1';

interface PersistedFilters {
  categoryIds: string[];
  familyIds: string[];
  search: string;
  sort: Sort;
  pageSize: number;
}

const defaultFilters: PersistedFilters = {
  categoryIds: [],
  familyIds: [],
  search: '',
  sort: 'popular',
  pageSize: 24,
};

function loadFilters(): PersistedFilters {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return defaultFilters;
    const parsed = JSON.parse(raw) as Partial<PersistedFilters> & {
      categoryId?: string | null;
      familyId?: string | null;
    };
    return {
      ...defaultFilters,
      ...parsed,
      categoryIds:
        parsed.categoryIds ?? (parsed.categoryId ? [parsed.categoryId] : []),
      familyIds: parsed.familyIds ?? (parsed.familyId ? [parsed.familyId] : []),
    };
  } catch {
    return defaultFilters;
  }
}

export function HomePage() {
  const { t } = useTranslation();
  const ds = useDataService();
  const qc = useQueryClient();
  // UN SEUL palier de prix dans tout l'espace : celui ACTIF du panier — la
  // « Base de prix par défaut » des Paramètres, ou le choix ponctuel Min/Moy/Max
  // fait depuis le panier. La grille, la fiche produit et le panier annoncent
  // donc le même prix, et c'est exactement celui qu'`addItem` enregistre.
  // (Avant : la grille était figée sur le palier Max — un prix que ni la fiche
  // produit ni le panier ne confirmaient.)
  const { addItem, tier: cartTier, setDrawerOpen } = useCart();

  const clientMode = useClientMode();

  const articles = useProArticles();
  const pricingQuery = useProArticlePricing();
  const proCategoriesQuery = useProPublishedCategories();
  const favoritesQuery = useProFavorites();

  const [searchParams, setSearchParams] = useSearchParams();

  // `?placed=<code>` est posé par le checkout en mode client.
  const placedCode = searchParams.get('placed');
  const dismissPlaced = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('placed');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const [filters, setFilters] = useState<PersistedFilters>(() => loadFilters());
  const [searchInput, setSearchInput] = useState(filters.search);
  const [page, setPage] = useState(1);
  const [detailArticle, setDetailArticle] = useState<ProArticle | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  // Deep link: the dashboard "preview" button opens /home?product=<articleId>
  // so the owner sees the product exactly as the electrician does. Open the
  // detail modal once the catalogue has loaded, then drop the param so a
  // refresh or close doesn't re-open it.
  useEffect(() => {
    const productId = searchParams.get('product');
    if (productId && articles.data) {
      const match = articles.data.find((a) => a.id === productId);
      if (match) setDetailArticle(match);
      const next = new URLSearchParams(searchParams);
      next.delete('product');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, articles.data, setSearchParams]);

  // debounced search
  useEffect(() => {
    const handle = setTimeout(
      () => setFilters((f) => ({ ...f, search: searchInput })),
      300,
    );
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Lock scroll when drawer is open
  useEffect(() => {
    if (!filterDrawerOpen) return undefined;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [filterDrawerOpen]);

  const pricingByArticle = useMemo(() => {
    const map = new Map<string, ProArticlePricing>();
    for (const p of pricingQuery.data ?? []) map.set(p.articleId, p);
    return map;
  }, [pricingQuery.data]);

  const favoriteIds = useMemo(
    () => new Set((favoritesQuery.data ?? []).map((f) => f.articleId)),
    [favoritesQuery.data],
  );

  // Catalog filter is driven by the PUBLISHED ProSpace categories (the
  // publishing layer managed from the dashboard). The hierarchy still mirrors
  // the article catalog via parentArticleCategoryId; an orphan (parent not
  // published) surfaces at the top level. Each node's `id` is the
  // article_category_id so product filtering (which keys off article
  // categories) keeps working unchanged.
  const categoryTree = useMemo(() => {
    const cats = proCategoriesQuery.data ?? [];
    const publishedArticleCatIds = new Set(cats.map((c) => c.articleCategoryId));
    const nameOf = (c: (typeof cats)[number]) =>
      c.nameFr ?? c.articleCategoryName ?? c.articleCategoryId;
    type Node = { id: string; name: string };
    const childrenByParent = new Map<string, Node[]>();
    const roots: Node[] = [];
    for (const c of cats) {
      const node: Node = { id: c.articleCategoryId, name: nameOf(c) };
      const parent = c.parentArticleCategoryId;
      if (!parent || !publishedArticleCatIds.has(parent)) {
        roots.push(node);
      } else {
        const list = childrenByParent.get(parent) ?? [];
        list.push(node);
        childrenByParent.set(parent, list);
      }
    }
    return { roots, childrenByParent };
  }, [proCategoriesQuery.data]);

  const filtered = useMemo(() => {
    const all = articles.data ?? [];
    const term = foldSearch(filters.search.trim());
    let list = all.filter((a) => {
      const catId = a.categoryId;
      const subId = a.subFamily;
      if (
        filters.categoryIds.length > 0 &&
        !((catId && filters.categoryIds.includes(catId)) ||
          (subId && filters.categoryIds.includes(subId)))
      )
        return false;
      if (
        filters.familyIds.length > 0 &&
        (!a.familyId || !filters.familyIds.includes(a.familyId))
      )
        return false;
      if (
        term &&
        !foldSearch(`${a.name} ${a.code} ${a.description ?? ''}`).includes(term)
      )
        return false;
      return true;
    });
    switch (filters.sort) {
      case 'price_asc':
        list = [...list].sort((a, b) => a.salePriceHt - b.salePriceHt);
        break;
      case 'price_desc':
        list = [...list].sort((a, b) => b.salePriceHt - a.salePriceHt);
        break;
      case 'name':
        list = [...list].sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }
    return list;
  }, [articles.data, filters]);

  useEffect(() => {
    setPage(1);
  }, [filters.search, filters.categoryIds, filters.familyIds, filters.sort]);

  const paged = useMemo(() => {
    const start = (page - 1) * filters.pageSize;
    return filtered.slice(start, start + filters.pageSize);
  }, [filtered, page, filters.pageSize]);

  const favorites = useMemo(() => {
    if ((favoritesQuery.data ?? []).length === 0) return [];
    return (articles.data ?? []).filter((a) => favoriteIds.has(a.id));
  }, [articles.data, favoritesQuery.data, favoriteIds]);

  const toggleFavorite = useMutation({
    mutationFn: async ({
      articleId,
      isFav,
    }: {
      articleId: string;
      isFav: boolean;
    }) => {
      if (isFav) await ds.proFavorites.remove(articleId);
      else await ds.proFavorites.add(articleId);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pro-favorites'] }),
  });

  const isLoading = articles.isLoading || pricingQuery.isLoading;

  // ─── Filter helpers ───
  const isCategorySelected = (id: string) => filters.categoryIds.includes(id);
  const isCategoryExpanded = (id: string) => expandedCategories.has(id);

  const toggleCategoryExpand = (id: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleCategory = (id: string) => {
    setFilters((f) => ({
      ...f,
      categoryIds: f.categoryIds.includes(id)
        ? f.categoryIds.filter((x) => x !== id)
        : [...f.categoryIds, id],
    }));
  };

  const selectAll = () =>
    setFilters((f) => ({ ...f, categoryIds: [], familyIds: [] }));

  const FilterPanel = (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3
          className="text-[16px] font-semibold"
          style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
        >
          {t('pro.home.category', 'Catégorie')}
        </h3>
        {(filters.categoryIds.length > 0 || filters.familyIds.length > 0) && (
          <button
            type="button"
            onClick={selectAll}
            className="text-[12px] font-medium"
            style={{ color: 'var(--pro-primary)', fontFamily: 'Figtree' }}
          >
            {t('pro.home.clear_filters', 'Effacer')}
          </button>
        )}
      </div>

      <ul className="flex flex-col gap-2">
        <li className="flex items-center justify-between gap-2 h-8">
          <Checkbox
            label={t('pro.home.all', 'Tous')}
            checked={
              filters.categoryIds.length === 0 && filters.familyIds.length === 0
            }
            onChange={selectAll}
          />
          {/* spacer so this row's right edge aligns with chevroned rows */}
          <span className="w-6 h-6 shrink-0" aria-hidden />
        </li>
        {categoryTree.roots.map((cat) => {
          const children = categoryTree.childrenByParent.get(cat.id) ?? [];
          const expanded = isCategoryExpanded(cat.id);
          return (
            <li key={cat.id} className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2 h-8">
                <Checkbox
                  label={cat.name}
                  checked={isCategorySelected(cat.id)}
                  onChange={() => toggleCategory(cat.id)}
                />
                {children.length > 0 ? (
                  <button
                    type="button"
                    aria-label={expanded ? 'Collapse' : 'Expand'}
                    onClick={() => toggleCategoryExpand(cat.id)}
                    className="w-6 h-6 shrink-0 rounded-md flex items-center justify-center hover:bg-neutral-100"
                    style={{ color: 'var(--pro-text-grey)' }}
                  >
                    {expanded ? (
                      <ChevronUp className="w-3.5 h-3.5" />
                    ) : (
                      <ChevronDown className="w-3.5 h-3.5" />
                    )}
                  </button>
                ) : (
                  <span className="w-6 h-6 shrink-0" aria-hidden />
                )}
              </div>
              {expanded && children.length > 0 && (
                <ul className="pl-3 flex flex-col gap-2 pro-fade-in">
                  {children.map((sub) => (
                    <li key={sub.id} className="flex items-center h-8">
                      <Checkbox
                        label={sub.name}
                        checked={isCategorySelected(sub.id)}
                        onChange={() => toggleCategory(sub.id)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <div className="grid lg:grid-cols-[207px_1fr] gap-4 items-start">
      {/* Confirmation après validation depuis le mode client : la fiche du
          document reste côté électricien (elle porte la commission), seul le
          numéro remonte ici. */}
      {placedCode && (
        <div
          className="lg:col-span-2 rounded-2xl px-4 py-3 flex items-center justify-between gap-3"
          style={{
            background: 'var(--pro-primary-subtle)',
            border: '1px solid var(--pro-primary-light)',
          }}
          role="status"
        >
          <span
            className="text-[14px] font-semibold"
            style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
          >
            {t('pro.client_mode.placed', 'Commande enregistrée · {{code}}', {
              code: placedCode,
            })}
          </span>
          <button
            type="button"
            onClick={dismissPlaced}
            aria-label={t('pro.client_mode.dismiss', 'Fermer')}
            className="shrink-0 p-1.5 rounded-lg hover:bg-white/60"
            style={{ color: 'var(--pro-text-grey)' }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Prise en main — masquée en mode client : c'est la formation de
          l'électricien, le client final n'a rien à y faire. La bannière se
          retire d'elle-même une fois le parcours terminé. */}
      {!clientMode && (
        <div className="lg:col-span-2">
          <CourseOnboardingBanner />
        </div>
      )}

      {/* Filter sidebar (desktop) */}
      {/* Le décalage de 76px aligne le panneau sous la barre de recherche en
          mode électricien. En mode client il n'y a pas d'en-tête de page à
          compenser : le panneau remonte, aligné sur la barre de recherche.
          `top-0` et non `top-4` : le conteneur de défilement commence sous
          l'en-tête, donc `top-4` collerait le panneau 16px plus bas DÈS le
          repos (sa position statique est déjà au-dessus du seuil de collage)
          — c'est le blanc que Khalil a entouré. */}
      <aside
        className={`hidden lg:block pro-card p-4 sticky ${
          clientMode ? 'top-0' : 'top-4 lg:mt-[76px]'
        }`}
      >
        {FilterPanel}
      </aside>

      {/* Filter drawer (mobile) */}
      {filterDrawerOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40 lg:hidden pro-fade-in"
            onClick={() => setFilterDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside
            className="fixed top-0 left-0 h-full w-[280px] max-w-[85vw] bg-white z-50 lg:hidden pro-slide-in-left p-5 overflow-y-auto"
            style={{
              borderRight: '1px solid var(--pro-line)',
              boxShadow: '4px 0 24px rgba(0,0,0,0.10)',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className="text-[16px] font-semibold"
                style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
              >
                {t('pro.home.filters', 'Filtres')}
              </h3>
              <button
                type="button"
                aria-label={t('pro.home.close', 'Fermer')}
                onClick={() => setFilterDrawerOpen(false)}
                className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-neutral-100"
              >
                <X
                  className="w-5 h-5"
                  style={{ color: 'var(--pro-text-dark)' }}
                />
              </button>
            </div>
            {FilterPanel}
          </aside>
        </>
      )}

      {/* Main content */}
      <div className="flex flex-col gap-4 min-w-0">
        {/* Toolbar: search + sort + mobile filter trigger */}
        <div className="pro-card p-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            aria-label={t('pro.home.open_filters', 'Ouvrir les filtres')}
            onClick={() => setFilterDrawerOpen(true)}
            className="lg:hidden inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[13px] font-medium"
            style={{
              border: '1px solid var(--pro-border)',
              color: 'var(--pro-text-dark)',
            }}
          >
            <Filter className="w-4 h-4" />
            {t('pro.home.filters', 'Filtres')}
            {filters.categoryIds.length + filters.familyIds.length > 0 && (
              <span
                className="ml-1 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold flex items-center justify-center text-white px-1"
                style={{ background: 'var(--pro-primary)' }}
              >
                {filters.categoryIds.length + filters.familyIds.length}
              </span>
            )}
          </button>

          <div className="relative flex-1 min-w-[160px]">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
              style={{ color: 'var(--pro-text-grey)' }}
            />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t(
                'pro.home.search_placeholder',
                'Rechercher un article…',
              )}
              className="w-full h-9 pl-10 pr-9 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-orange-300"
              style={{
                background: 'var(--pro-grey-subtle)',
                color: 'var(--pro-text-dark)',
              }}
            />
            {searchInput && (
              <button
                type="button"
                aria-label={t('pro.home.clear_search', 'Effacer')}
                onClick={() => setSearchInput('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-neutral-200"
              >
                <X
                  className="w-4 h-4"
                  style={{ color: 'var(--pro-text-grey)' }}
                />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <span
              className="hidden sm:inline text-[13px]"
              style={{ color: 'var(--pro-text-grey)' }}
            >
              {t('pro.home.sort_by', 'Trier par:')}
            </span>
            <Dropdown<Sort>
              value={filters.sort}
              onChange={(sort) => setFilters({ ...filters, sort })}
              variant="outline"
              size="md"
              options={[
                {
                  key: 'popular',
                  label: t('pro.home.sort_popular', 'Populaire'),
                },
                {
                  key: 'price_asc',
                  label: t('pro.home.sort_price_asc', 'Prix ↑'),
                },
                {
                  key: 'price_desc',
                  label: t('pro.home.sort_price_desc', 'Prix ↓'),
                },
                { key: 'name', label: t('pro.home.sort_name', 'Nom') },
              ]}
            />
          </div>
        </div>

        {/* Favorites section — outil de l'électricien, masqué en mode client */}
        {!clientMode && !isLoading && favorites.length > 0 && (
          <div className="pro-card p-4 pro-fade-in">
            <div className="flex items-center justify-between mb-3">
              <h3
                className="text-[16px] font-semibold flex items-center gap-2"
                style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
              >
                <Bookmark
                  className="w-4 h-4"
                  style={{
                    color: 'var(--pro-primary)',
                    fill: 'var(--pro-primary)',
                  }}
                />
                {t('pro.home.favorites', 'Produits préférés')}
                <span
                  className="text-[14px] font-normal"
                  style={{ color: 'var(--pro-text-grey)' }}
                >
                  {favorites.length}
                </span>
              </h3>
              {favorites.length > 5 && (
                <button
                  type="button"
                  onClick={() =>
                    setFilters((f) => ({
                      ...f,
                      categoryIds: [],
                      familyIds: [],
                    }))
                  }
                  className="inline-flex items-center gap-1 text-[13px] font-medium"
                  style={{ color: 'var(--pro-primary)', fontFamily: 'Figtree' }}
                >
                  {t('pro.home.view_all', 'Voir tout')}
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="pro-product-grid">
              {favorites.slice(0, 5).map((article) => {
                const pricing = pricingByArticle.get(article.id) ?? null;
                return (
                  <ProductCard
                    key={article.id}
                    article={article}
                    pricing={pricing}
                    tier={cartTier}
                    isFavorite
                    onClick={() => setDetailArticle(article)}
                    onToggleFavorite={() =>
                      toggleFavorite.mutate({
                        articleId: article.id,
                        isFav: true,
                      })
                    }
                    onAddToCart={() => addItem(article, pricing, 1)}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Products grid */}
        {isLoading ? (
          <div className="pro-product-grid">
            {Array.from({ length: 10 }).map((_, i) => (
              <div
                key={i}
                className="aspect-square rounded-2xl pro-fade-in"
                style={{ background: 'var(--pro-grey-subtle)' }}
              />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          /* Une recherche qui ne donne rien EST le moment de demander le
             produit : le libellé tapé devient le libellé de la demande. */
          <div className="pro-card p-12 text-center pro-fade-in">
            <p style={{ color: 'var(--pro-text-grey)' }}>
              {t('pro.home.empty', 'Aucun article trouvé')}
            </p>
            <p
              className="text-[13px] mt-1"
              style={{ color: 'var(--pro-text-light)' }}
            >
              {t(
                'pro.request.not_in_catalog',
                "Ce produit n'est pas dans notre catalogue ?",
              )}
            </p>
            <button
              type="button"
              onClick={() => setRequestOpen(true)}
              className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-xl text-[14px] font-semibold text-white hover:opacity-90"
              style={{ background: 'var(--pro-primary)', fontFamily: 'Figtree' }}
            >
              <PackageSearch className="w-4 h-4" />
              {t('pro.request.cta', 'Demander un produit')}
            </button>
          </div>
        ) : (
          <>
            <div className="pro-product-grid">
              {paged.map((article) => {
                const pricing = pricingByArticle.get(article.id) ?? null;
                const isFav = favoriteIds.has(article.id);
                return (
                  <ProductCard
                    key={article.id}
                    article={article}
                    pricing={pricing}
                    tier={cartTier}
                    isFavorite={isFav}
                    onClick={() => setDetailArticle(article)}
                    onToggleFavorite={() =>
                      toggleFavorite.mutate({ articleId: article.id, isFav })
                    }
                    onAddToCart={() => addItem(article, pricing, 1)}
                  />
                );
              })}
            </div>

            <Pagination
              page={page}
              pageSize={filters.pageSize}
              total={filtered.length}
              onPageChange={setPage}
              onPageSizeChange={(size) =>
                setFilters({ ...filters, pageSize: size })
              }
              pageSizeOptions={[12, 24, 48, 96]}
            />
          </>
        )}
      </div>

      <ProductDetailModal
        article={detailArticle}
        pricing={
          detailArticle
            ? (pricingByArticle.get(detailArticle.id) ?? null)
            : null
        }
        isFavorite={detailArticle ? favoriteIds.has(detailArticle.id) : false}
        onToggleFavorite={() => {
          if (detailArticle) {
            toggleFavorite.mutate({
              articleId: detailArticle.id,
              isFav: favoriteIds.has(detailArticle.id),
            });
          }
        }}
        onClose={() => setDetailArticle(null)}
        onAddToCart={(qty) => {
          if (detailArticle) {
            addItem(
              detailArticle,
              pricingByArticle.get(detailArticle.id) ?? null,
              qty,
            );
            setDetailArticle(null);
          }
        }}
      />

      {/* Le terme cherché devient le libellé de la demande : l'électricien a
          déjà écrit ce qu'il veut, le lui refaire taper serait absurde. */}
      <RequestProductDialog
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        initialLabel={filters.search.trim()}
        onAdded={() => setDrawerOpen(true)}
      />
    </div>
  );
}

function Checkbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer text-[14px] select-none w-full">
      <span
        className="w-[14px] h-[14px] rounded-[3px] flex items-center justify-center shrink-0"
        style={{
          background: checked ? 'var(--pro-primary)' : 'white',
          border: checked
            ? '1px solid var(--pro-primary)'
            : '1px solid var(--pro-border)',
          transition:
            'background 280ms var(--pro-ease), border-color 280ms var(--pro-ease)',
        }}
      >
        {checked && (
          <svg
            width="9"
            height="9"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="sr-only"
      />
      <span
        className="flex-1 truncate"
        style={{
          color: checked ? 'var(--pro-text-dark)' : 'var(--pro-text-dark)',
          fontFamily: 'Figtree',
          fontWeight: checked ? 500 : 400,
        }}
      >
        {label}
      </span>
    </label>
  );
}
